import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlatformSettings({ user }) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Only admins can access platform settings');
      navigate('/dashboard');
      return;
    }
    fetchSettings();
  }, [user]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/platform`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PNG, JPG, JPEG, WEBP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/settings/platform-logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setSettings(prev => ({ ...prev, platform_logo_url: response.data.platform_logo_url }));
      toast.success('Platform logo updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="mb-4 -ml-2 text-[#AFAFAF] hover:text-[#EAE7E1]" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-[#EAE7E1] tracking-tight">Platform Settings</h1>
          <p className="mt-1 text-base text-[#AFAFAF]">Admin-only: VelvetGate platform branding</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6">
          <h2 className="text-xl font-semibold text-[#EAE7E1] mb-6 tracking-tight">Platform Logo (VelvetGate Branding)</h2>
          <p className="text-sm text-[#AFAFAF] mb-4">This is the global VelvetGate logo. It appears on all tickets, emails, and "Powered by VelvetGate" sections. Only admins can change this.</p>

          {settings?.platform_logo_url && (
            <div className="mb-6 p-4 bg-[#050505] border border-[#1A1A1A] rounded-sm inline-block">
              <img
                src={`${BACKEND_URL}${settings.platform_logo_url}`}
                alt="Platform Logo"
                className="max-h-24"
                data-testid="platform-logo-preview"
              />
            </div>
          )}

          <div className="flex items-center space-x-4">
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={(e) => e.target.files[0] && handleLogoUpload(e.target.files[0])}
              className="hidden"
              id="logo-upload"
              data-testid="logo-upload-input"
            />
            <Button
              onClick={() => document.getElementById('logo-upload').click()}
              disabled={uploading}
              className="bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold"
              data-testid="logo-upload-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : (settings?.platform_logo_url ? 'Change Logo' : 'Upload Logo')}
            </Button>
            <span className="text-xs text-[#AFAFAF]">PNG, JPG, or WEBP. Max 5MB.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
